import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiShoppingBag, FiTrendingUp, FiCalendar, FiPackage, FiBarChart2, FiRefreshCw } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FaRupeeSign } from 'react-icons/fa';


const ConsolidatedSales = () => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('month');

  useEffect(() => {
    fetchSalesData();
  }, [filterType]);

  const fetchSalesData = async () => {
    try {
      const response = await api.get(`/vendor-owner/sales/consolidated?filterType=${filterType}`);
      if (response.data.success) {
        setSalesData(response.data.data);
        toast.success('Sales data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch sales data');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading sales data...</p>
        </div>
      </div>
    );
  }

  if (!salesData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Consolidated Sales Report</h1>
          <p className="text-gray-600">Comprehensive sales analytics across all your restaurants</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiBarChart2 className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No Sales Data Available</h3>
          <p className="text-gray-600">
            There are no sales records for the selected period. Sales data will appear here once orders are placed.
          </p>
        </div>
      </div>
    );
  }

  const { summary, restaurantSales, recentOrders } = salesData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Consolidated Sales Report</h1>
            <p className="text-gray-600">Comprehensive sales analytics across all your restaurants</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={fetchSalesData}
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-2 mt-6">
          <button 
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 ${
              filterType === 'today' 
                ? 'bg-indigo-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setFilterType('today')}
          >
            <FiCalendar size={16} />
            Today
          </button>
          <button 
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 ${
              filterType === 'week' 
                ? 'bg-indigo-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setFilterType('week')}
          >
            <FiCalendar size={16} />
            This Week
          </button>
          <button 
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 ${
              filterType === 'month' 
                ? 'bg-indigo-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setFilterType('month')}
          >
            <FiCalendar size={16} />
            This Month
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiShoppingBag size={28} />
            </div>
          </div>
          <div>
            <div className="text-sm text-blue-100 mb-1">Total Restaurants</div>
            <div className="text-3xl font-bold">{summary.restaurantCount}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FaRupeeSign size={28} />
            </div>
          </div>
          <div>
            <div className="text-sm text-green-100 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold">{formatCurrency(summary.totalRevenue)}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiPackage size={28} />
            </div>
          </div>
          <div>
            <div className="text-sm text-purple-100 mb-1">Total Orders</div>
            <div className="text-3xl font-bold">{summary.totalOrders}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiTrendingUp size={28} />
            </div>
          </div>
          <div>
            <div className="text-sm text-orange-100 mb-1">Average Order</div>
            <div className="text-3xl font-bold">
              {formatCurrency(summary.totalOrders > 0 ? summary.totalRevenue / summary.totalOrders : 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <FiBarChart2 className="text-indigo-600" />
            Sales by Restaurant
          </h3>
          <p className="text-gray-600 text-sm">Revenue and order comparison across locations</p>
        </div>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={restaurantSales} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="restaurantName" 
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
              />
              <Bar dataKey="totalRevenue" fill="#2596be" name="Revenue" radius={[8, 8, 0, 0]} />
              <Bar dataKey="totalOrders" fill="#4caf50" name="Orders" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recent Orders</h3>
            <p className="text-gray-600 text-sm">Latest transactions across all restaurants</p>
          </div>
          <div className="bg-indigo-50 rounded-xl px-4 py-2">
            <span className="text-2xl font-bold text-indigo-600">{recentOrders.length}</span>
            <span className="text-sm text-indigo-700 ml-2">orders</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Order ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Restaurant</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Customer</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders && recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <tr key={order.orderNumber} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm font-medium text-indigo-600">#{order.orderNumber}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{order.vendorId?.restaurantDetails?.restaurantName || 'N/A'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{order.customerId?.fullName || 'Guest'}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{formatCurrency(order.totalRevenue || order.pricing?.total || 0)}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(order.createdAt).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiPackage className="text-gray-400 text-2xl" />
                    </div>
                    <div className="text-lg font-medium text-gray-900 mb-2">No Recent Orders</div>
                    <div className="text-sm text-gray-600">
                      Orders will appear here once customers place orders at your restaurants.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedSales;
